import type { Environment } from "../../../types";
import { CHAIN_IDS } from "../constants";
import { getApiBase } from "../../../lib/remote-config";
import type { LpBalanceInfo } from "../types";

// host(wagmi 보유 측)가 `lpBalanceReader`를 주입하지 않은 dApp을 위한 내부 폴백.
// dapp-ui는 viem/wagmi를 직접 쓰지 못하므로, send 기능의 gas-rpc 폴백과 동일하게
// chain-info API가 알려준 RPC URL로 raw JSON-RPC `eth_call`을 보내 LP 토큰의
// balanceOf / totalSupply / decimals를 직접 읽는다.
//
// 모든 wei-scale 값은 BigInt로 처리한다 (CLAUDE.md `NUMERIC_HANDLING`).

/** keccak256 selector. `balanceOf(address)` */
const BALANCE_OF_SELECTOR = "0x70a08231";
/** `totalSupply()` */
const TOTAL_SUPPLY_SELECTOR = "0x18160ddd";
/** `decimals()` */
const DECIMALS_SELECTOR = "0x313ce567";

const RPC_TIMEOUT_MS = 8_000;

interface JsonRpcResponse<T> {
  jsonrpc: "2.0";
  id: number | string;
  result?: T;
  error?: { code: number; message: string };
}

interface ChainInfoPayload {
  rpc?: string;
}

interface ChainInfoEnvelope {
  data?: ChainInfoPayload | ChainInfoPayload[];
}

async function rpcCall<T>(
  rpcUrl: string,
  method: string,
  params: unknown[],
  signal?: AbortSignal,
): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), RPC_TIMEOUT_MS);
  if (signal) {
    signal.addEventListener("abort", () => controller.abort(), { once: true });
  }
  const res = await fetch(rpcUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params }),
    signal: controller.signal,
  }).finally(() => clearTimeout(timeout));
  if (!res.ok) throw new Error(`RPC ${method} HTTP ${res.status}`);
  const json = (await res.json()) as JsonRpcResponse<T>;
  if (json.error) throw new Error(`RPC ${method}: ${json.error.message}`);
  if (json.result === undefined) throw new Error(`RPC ${method}: empty result`);
  return json.result;
}

/** 32바이트(64 hex)로 우측 정렬 패딩된 address 인자. */
function encodeAddressArg(address: string): string {
  return address.toLowerCase().replace(/^0x/, "").padStart(64, "0");
}

/** 빈 결과(`0x`)/디코드 실패는 undefined로 흡수한다. */
export function toBigIntOrUndefined(hex: string | undefined): bigint | undefined {
  if (!hex || hex === "0x") return undefined;
  try {
    return BigInt(hex);
  } catch {
    return undefined;
  }
}

export async function ethCall(
  rpcUrl: string,
  to: string,
  data: string,
  signal?: AbortSignal,
  from?: string,
): Promise<string | undefined> {
  try {
    return await rpcCall<string>(
      rpcUrl,
      "eth_call",
      [from ? { from, to, data } : { to, data }, "latest"],
      signal,
    );
  } catch {
    return undefined;
  }
}

/** 단일 pair(LP)의 balanceOf/totalSupply/decimals를 병렬로 읽는다. */
async function readPair(
  rpcUrl: string,
  account: string,
  pair: string,
  signal?: AbortSignal,
): Promise<LpBalanceInfo | null> {
  const [balanceHex, supplyHex, decimalsHex] = await Promise.all([
    ethCall(rpcUrl, pair, BALANCE_OF_SELECTOR + encodeAddressArg(account), signal),
    ethCall(rpcUrl, pair, TOTAL_SUPPLY_SELECTOR, signal),
    ethCall(rpcUrl, pair, DECIMALS_SELECTOR, signal),
  ]);

  const balance = toBigIntOrUndefined(balanceHex);
  // balanceOf는 필수 — 읽지 못하면 해당 pair는 주입형 reader 미주입과 동일하게
  // 잔고 미상으로 두고 맵에서 제외한다(섹션은 풀 정보만 표시).
  if (balance === undefined) return null;

  const decimalsRaw = toBigIntOrUndefined(decimalsHex);
  return {
    balance,
    totalSupply: toBigIntOrUndefined(supplyHex),
    decimals: decimalsRaw !== undefined ? Number(decimalsRaw) : undefined,
  };
}

/** CROSS 체인 RPC URL을 wallet chain-info API에서 조회한다. */
export async function fetchCrossRpcUrl(
  env: Environment,
  signal?: AbortSignal,
): Promise<string | undefined> {
  const chainId = CHAIN_IDS[env].cross;
  const walletApi = await getApiBase("walletServer", env);
  const res = await fetch(`${walletApi}/v1/public/chain/info/${chainId}`, {
    cache: "no-store",
    signal,
  });
  if (!res.ok) return undefined;
  const json = (await res.json()) as ChainInfoEnvelope | ChainInfoPayload;
  const payload =
    "data" in json && json.data !== undefined
      ? Array.isArray(json.data)
        ? json.data[0]
        : json.data
      : (json as ChainInfoPayload);
  return payload?.rpc || undefined;
}

/**
 * 주입형 `lpBalanceReader` 미주입 시 사용하는 내부 폴백 reader.
 * CROSS 체인 RPC로 직접 LP 잔고를 읽어 `LpBalanceReaderFn`과 동일한 형태로 반환한다.
 * RPC URL 조회/호출이 실패하면 빈 맵을 돌려 섹션이 풀 정보만 표시하도록 폴백한다.
 */
export async function readLpBalancesViaRpc(
  env: Environment,
  account: string,
  pairAddresses: string[],
  signal?: AbortSignal,
): Promise<Record<string, LpBalanceInfo>> {
  if (!account || pairAddresses.length === 0) return {};

  let rpcUrl: string | undefined;
  try {
    rpcUrl = await fetchCrossRpcUrl(env, signal);
  } catch {
    rpcUrl = undefined;
  }
  if (!rpcUrl) return {};

  const entries = await Promise.all(
    pairAddresses.map(
      async (pair) =>
        [pair.toLowerCase(), await readPair(rpcUrl as string, account, pair, signal)] as const,
    ),
  );

  const out: Record<string, LpBalanceInfo> = {};
  for (const [addr, info] of entries) {
    if (info) out[addr] = info;
  }
  return out;
}
